import * as React from 'react';

import { DataGroupListResponse } from '@ariksa/abac/api';
import { CloudAccountInfoGetResponse } from '@ariksa/jit/api';
import { Box, HStack, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  CustomTooltip,
  EllipsisText,
  formatTooltip,
  getIconColor,
  renderTableHeaderWithLoader,
  renderTooltipFieldValue,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { Select } from 'components/DataEntry';
import {
  EditIcon,
  IconTypes,
  PrincipalAccessIcon,
  ThumbsUpIcon,
  TrashIcon,
  UserIcon,
} from 'components/Icons';
import { DeleteConfirmationModal, TableActionsMenu } from 'components/Overlay';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { ConfigureJitApproval } from 'containers/Jit/Components/Configuration/ConfigureJitApproval';
import { ConfigureJITApprovers } from 'containers/Jit/Components/Configuration/ConfigureJITApprovers';
import { renderDataGroupSensitivityLabels } from 'containers/Jit/Components/utils';
import { ConfigureAccessToDataGroup } from 'containers/Jit/DataGroups/Components/ConfigureAccessToDataGroup';
import { DataGroupDrawer } from 'containers/Jit/DataGroups/Components/Drawer/DataGroupDrawer';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { useSearchParams } from 'hooks/useSearchParams';
import { formatDate } from 'utils/date';

import { selectDataGroups } from '../selectors';
import { actions } from '../slice';

import {
  DataGroupFilters,
  filterDataGroupRows,
  getDataGroupFiltersFromParams,
  getDataGroupFilterOptions,
} from './utils';

const toJitAccount = (
  dataGroup: DataGroupListResponse,
): CloudAccountInfoGetResponse => ({
  uuid: dataGroup.account_id,
  cloud_account_id: null,
  cloud_type: dataGroup.cloud_provider ?? '',
  jit_subscription: '',
  created_date: dataGroup.created_at,
  last_updated: dataGroup.updated_at,
});

export const DataGroupTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useSearchParams<Partial<DataGroupFilters>>();
  const { updateValuesInURL } = useSearchParamFunctions();
  const { renderStackedAccountNameID } = useCloudAccountId();
  const {
    dataGroups,
    dataGroupAction,
    dataGroupRequestCounts,
    dataGroupSensitivity,
  } = useSelector(selectDataGroups);
  const dataGroupAccess = useDisclosure();
  const approversDisclosure = useDisclosure();
  const autoApprovalDisclosure = useDisclosure();
  const deleteConfirmation = useDisclosure();
  const entitiesDisclosure = useDisclosure();

  const [
    currentRecord,
    setCurrentRecord,
  ] = React.useState<DataGroupListResponse>({} as DataGroupListResponse);
  const [filters, setFilters] = React.useState<DataGroupFilters>(() =>
    getDataGroupFiltersFromParams(params),
  );

  React.useEffect(() => {
    setFilters(getDataGroupFiltersFromParams(params));
  }, [params]);

  const handleFilterChange = (
    filter: keyof DataGroupFilters,
    value?: string,
  ) => {
    setFilters(current => ({ ...current, [filter]: value }));
    updateValuesInURL({ [filter]: value });
  };

  const dataGroupRows = React.useMemo(
    () =>
      (dataGroups.data ?? []).map(dataGroup => ({
        ...dataGroup,
        sensitivity_categories:
          dataGroupSensitivity?.[dataGroup.uuid]?.data
            ?.sensitivity_categories ?? [],
      })),
    [dataGroups.data, dataGroupSensitivity],
  );

  const sensitivityLoading = dataGroupRows.some(
    dataGroup => dataGroupSensitivity?.[dataGroup.uuid]?.isLoading,
  );

  const filteredDataGroupRows = React.useMemo(
    () => filterDataGroupRows(dataGroupRows, filters),
    [dataGroupRows, filters],
  );

  const cloudOptions = React.useMemo(
    () => getDataGroupFilterOptions(dataGroupRows, 'cloud'),
    [dataGroupRows],
  );
  const ownerOptions = React.useMemo(
    () => getDataGroupFilterOptions(dataGroupRows, 'owner'),
    [dataGroupRows],
  );
  const sensitivityOptions = React.useMemo(
    () => getDataGroupFilterOptions(dataGroupRows, 'sensitivity'),
    [dataGroupRows],
  );

  const handleDelete = () => {
    dispatch(
      actions.deleteDataGroup({
        q: { dataGroupId: currentRecord.uuid },
        onSuccess: () => {
          deleteConfirmation.onClose();
          dispatch(
            actions.getDataGroups({
              q: {
                page: dataGroups.page.info.page_number,
                size: dataGroups.page.info.page_size,
              },
            }),
          );
        },
      }),
    );
  };

  const menuItems = (row: DataGroupListResponse) => [
    {
      label: 'Edit data group',
      icon: <EditIcon />,
      onClick: () => navigate(`edit/${row?.uuid}`),
    },
    {
      label: 'Configure access',
      icon: <PrincipalAccessIcon />,
      onClick: () => {
        setCurrentRecord(row);
        dataGroupAccess.onOpen();
      },
    },
    {
      label: 'Set approvers',
      icon: <UserIcon />,
      onClick: () => {
        setCurrentRecord(row);
        approversDisclosure.onOpen();
      },
    },
    {
      label: 'Auto-approval policy',
      icon: <ThumbsUpIcon />,
      onClick: () => {
        setCurrentRecord(row);
        autoApprovalDisclosure.onOpen();
      },
    },
    {
      label: 'Delete data group',
      icon: <TrashIcon color="red" />,
      onClick: () => {
        setCurrentRecord(row);
        deleteConfirmation.onOpen();
      },
      isDisabled: row?.is_used_by_policy,
    },
  ];

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.DataGroup}
          color={getIconColor(row?.cloud_provider!)}
        >
          <StackedCell
            upper={row?.name}
            lower={row?.team}
            showLowerTooltip={false}
            showUpperTooltip={false}
            tooltip={formatTooltip({
              header: 'Data Group',
              content: (
                <Stack spacing={1}>
                  {renderTooltipFieldValue('Name', row?.name ?? '-')}
                  {renderTooltipFieldValue('Team', row?.team ?? '-')}
                </Stack>
              ),
              width: 64,
            })}
          />
          {/*<HStack>
            <EllipsisText label={row?.name} />
            {!row?.has_access_assignments && (
              <CustomTooltip label="Configure access" w={20} textAlign="center">
                <Box boxSize={5} color={customTheme.colors.critical}>
                  <Center>
                    <ExclamationTriangleIcon />
                  </Center>
                </Box>
              </CustomTooltip>
            )}
          </HStack>*/}
        </WithResourceIcon>
      ),
    },
    {
      header: 'Description',
      align: 'left',
      render: ({ row }) => (
        <Text whiteSpace="wrap" noOfLines={2}>
          {row?.description}
        </Text>
      ),
      styles: { maxW: '35%', cell: { w: '35%' } },
    },
    {
      header: 'Source',
      accessor: 'account_id',
      align: 'left',
      render: ({ value }) => renderStackedAccountNameID(value, 'center'),
    },
    {
      header: renderTableHeaderWithLoader('Sensitivity', sensitivityLoading),
      align: 'left',
      render: ({ row }) =>
        renderDataGroupSensitivityLabels(row?.sensitivity_categories),
    },
    {
      header: 'JIT Access',
      align: 'left',
      render: ({ row }) => {
        const requests = dataGroupRequestCounts.data?.data_groups?.find(
          dataGroup => dataGroup.data_group_id === row?.uuid,
        );
        const roleCount = row?.jit_eligible_role_count ?? 0;
        const activeCount = requests?.active_requests_count ?? 0;

        return (
          <StackedCell
            upper={`${roleCount} role${roleCount === 1 ? '' : 's'}`}
            lower={activeCount ? `${activeCount} active` : 'None active'}
            showUpperTooltip={false}
            showLowerTooltip={false}
            styles={{
              lower: {
                color: activeCount
                  ? customTheme.colors.primary
                  : customTheme.colors.gray['250'],
              },
            }}
          />
        );
      },
    },
    {
      header: 'Owner',
      accessor: 'owner',
      align: 'left',
      render: ({ row }) => {
        const owner = row?.owner?.name ?? row?.owner?.id ?? '-';

        return (
          <CustomTooltip
            label={
              <Stack spacing={1}>
                {renderTooltipFieldValue('Owner', owner)}
                {renderTooltipFieldValue(
                  'Created',
                  row?.created_at ? formatDate(row.created_at) : '-',
                )}
                {renderTooltipFieldValue('Created by', row?.created_by ?? '-')}
              </Stack>
            }
          >
            <EllipsisText label={owner} />
          </CustomTooltip>
        );
      },
    },
    {
      header: 'Actions',
      render: ({ row }) => (
        <Box onClick={e => e.stopPropagation()}>
          <TableActionsMenu menuItems={menuItems(row)} />
        </Box>
      ),
    },
  ];

  return (
    <BorderedBox w="full" h="full">
      <HStack spacing={2} flexWrap="wrap" pb={4}>
        <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
        <Select
          options={cloudOptions}
          value={cloudOptions.find(option => option.value === filters.cloud)}
          onChange={option => handleFilterChange('cloud', option?.value)}
          menuPortalTarget={document.body}
          isAFilter
          leftMessage="Cloud"
          showUndefinedValueLabel={false}
        />
        <Select
          options={ownerOptions}
          value={ownerOptions.find(option => option.value === filters.owner)}
          onChange={option => handleFilterChange('owner', option?.value)}
          menuPortalTarget={document.body}
          isAFilter
          leftMessage="Owner"
          showUndefinedValueLabel={false}
        />
        <Select
          options={sensitivityOptions}
          value={sensitivityOptions.find(
            option => option.value === filters.sensitivity,
          )}
          onChange={option => handleFilterChange('sensitivity', option?.value)}
          menuPortalTarget={document.body}
          isAFilter
          leftMessage="Sensitivity"
          showUndefinedValueLabel={false}
          isLoading={sensitivityLoading}
          isDisabled={sensitivityLoading}
        />
      </HStack>
      <Table
        columns={columns}
        data={filteredDataGroupRows}
        isLoading={dataGroups.isLoading}
        onRowClick={row => {
          entitiesDisclosure.onOpen();
          setCurrentRecord(row);
        }}
        cursor="pointer"
      />
      {approversDisclosure.isOpen && currentRecord.uuid && (
        <ConfigureJITApprovers
          account={toJitAccount(currentRecord)}
          dataGroupId={currentRecord.uuid}
          dataGroupName={currentRecord.name}
          viewBy="data"
          isOpen={approversDisclosure.isOpen}
          onClose={approversDisclosure.onClose}
        />
      )}
      {autoApprovalDisclosure.isOpen && currentRecord.uuid && (
        <ConfigureJitApproval
          account={toJitAccount(currentRecord)}
          dataGroupId={currentRecord.uuid}
          dataGroupName={currentRecord.name}
          isOpen={autoApprovalDisclosure.isOpen}
          onClose={autoApprovalDisclosure.onClose}
        />
      )}
      {deleteConfirmation.isOpen && currentRecord.uuid && (
        <DeleteConfirmationModal
          name={currentRecord.name}
          type="data group"
          label="Delete data group"
          isOpen={deleteConfirmation.isOpen}
          onClose={deleteConfirmation.onClose}
          onConfirm={handleDelete}
          isLoading={dataGroupAction.isLoading}
        />
      )}
      {dataGroupAccess.isOpen && (
        <ConfigureAccessToDataGroup
          isOpen={dataGroupAccess.isOpen}
          onClose={dataGroupAccess.onClose}
          dataGroup={currentRecord}
        />
      )}
      {entitiesDisclosure.isOpen && (
        <DataGroupDrawer
          isOpen={entitiesDisclosure.isOpen}
          onClose={entitiesDisclosure.onClose}
          row={currentRecord}
        />
      )}
    </BorderedBox>
  );
};
